import {
  makeStyles,
  ConfirmationDialog,
  DetailDocList,
  DetailsContainer,
  DocsContainer,
  EditTagsDialog,
  Grid,
  Hidden,
  MapContainer,
  Tab,
  Tabs,
  TagContainer,
  useClientOptions,
  useCrmDialogs,
  useMediaQuery,
  useUser,
  useUserPermissions,
  useIsMobile
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import StarIcon from '@material-ui/icons/Star'
import type { Company, Contact, Deal, NoteActivity } from '@paintscout/api'
import { useContactQuery, useSaveEventMutation } from '@paintscout/api'
import { removeAuth0Prefix } from '@paintscout/util'
import {
  formatJobAddress,
  formatName,
  formatPhoneNumber,
  getFeatures,
  getObjectLabels,
  getTags,
  isUserAssigned,
  isUserOwner,
  uuid
} from '@paintscout/util/builder'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { motion } from 'framer-motion'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useState } from 'react'
import { Redirect, useRouteMatch, useHistory, useLocation } from 'react-router-dom'
import { useContactMutations, useDealMutations, useNoteMutations } from 'shared/hooks'
import DocumentTitle from 'src/components/common/DocumentTitle'
import { ContactDialog, CreateQuoteDialog, DealDialog, EventDialog, TaskDialog } from 'src/components/dialogs'
import BasePage from 'src/components/layouts/BasePage'
import { Sentry } from 'src/util/appSentry'

import { EditItemFilesDialog, EditIntegrationItemFilesDialog } from '@paintscout/react-quote'
import { jsonToLucene } from 'json-to-lucene'
import uniq from 'lodash/uniq'
import type { CalendarEvent, QuoteDocument, QuoteFile, QuoteTag } from 'paintscout'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import DealDocList from 'src/components/common/DealTable/DealDocList'
import EventDocList from 'src/components/common/EventTable/EventDocList'
import NotesDocList from 'src/components/common/NotesDocList'
import FilesDocList from 'src/components/common/QuoteTable/FilesDocList'
import QuoteDocList from 'src/components/common/QuoteTable/QuoteDocList'
import TaskDocList from 'src/components/common/TaskTable/TaskDocList'
import NoteDialog from 'src/components/dialogs/NoteDialog'
import { goToCompany, goToDeal } from 'src/util/routing'
import striptags from 'striptags'
import { getContactSourceDetail } from 'src/components/common/sourceDetails'
import { ChatActivityComponent } from '../../dashboard/Dashboard/ChatActivityComponent'
import DashboardActivity from '../../dashboard/Dashboard/DashboardActivity'
import OwnedHeader from '../OwnedHeader/OwnedHeader'
import { getDocFiles } from '../EditCompany/EditCompany'
import EditContactShim from './EditContactShim'

const DETAILS_ACTIVITY_TYPE = 'details.activityType'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    body: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
      marginTop: theme.spacing(2),
      borderRadius: theme.borderRadius.lg,
      width: '100%'
    },
    gridItem: {
      marginBottom: theme.spacing,
      '& > div': {
        marginBottom: theme.spacing(2)
      }
    },
    preferredStar: {
      color: '#f5a622',
      fontSize: '0.75rem',
      marginBottom: theme.spacing(0.25),
      marginLeft: theme.spacing(0.5)
    },
    tabs: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }),
  { name: 'EditContact' }
)

function EditContact() {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const isMobile = useIsMobile()
  const { crm: crmFlag } = useFlags()
  const { options } = useClientOptions()
  const features = getFeatures({ options })
  const { enqueueSnackbar } = useSnackbar()
  const match = useRouteMatch<{ id: string }>()
  const id = decodeURIComponent(match.params.id)
  const { user, trialExpired, isCrew } = useUser()
  const objectLabels = getObjectLabels({ options })
  const { openDialog, dismissDialog, dismissAllDialogs, setCrmContext } = useCrmDialogs()

  const {
    permissionToCreateQuote,
    permissionToCreateInvoice,
    permissionToEditAssignedContacts,
    permissionToEditAllContacts
  } = useUserPermissions()
  const isPrint = useMediaQuery('print')
  const isSm = useMediaQuery('sm')
  const isLg = !isSm || isPrint
  const { saveDeal } = useDealMutations()
  const [saveEvent] = useSaveEventMutation()
  const { deleteNote, updateNote, createNote } = useNoteMutations()
  const { saveContact, deleteContacts } = useContactMutations()
  const { loading, data, error } = useContactQuery({ variables: { id }, fetchPolicy: 'cache-and-network' })

  const [refreshToggle, setRefreshToggle] = useState(false)
  const [activityRefreshToggle, setActivityRefreshToggle] = useState(false)
  const [contact, setContact] = useState(null)
  const [activeDialog, setActiveDialog] = useState('')
  const hasTags = !!(features.quotes?.tags?.enabled || crmFlag)
  const hasSources = !!(features.contactLeadSources?.enabled || crmFlag)

  const contactTags = useMemo(() => getTags({ options, keys: contact?.tags ?? [] }), [contact?.tags, options])
  const contactFiles = useMemo(() => getDocFiles(contact), [contact])

  const {
    activityTab = 'all',
    eventTab = isMobile ? 'details' : crmFlag && !isCrew ? 'events' : 'quotes',
    viewTab: _viewTab = 'details'
  } = getUrlQuery<{
    activityTab: string
    eventTab: string
    viewTab: 'details' | 'activities'
  }>({ location })
  const [viewTab, setViewTab] = useState(_viewTab)

  const eventsTabList = useMemo(
    () =>
      [
        isMobile ? { label: 'Details', value: 'details', active: eventTab === 'details' } : null,
        crmFlag && !isCrew && { label: 'Events', value: 'events', active: eventTab === 'events' },
        crmFlag && !isCrew && { label: 'Deals', value: 'deals', active: eventTab === 'deals' },
        {
          label: objectLabels.quote.plural,
          value: 'quotes',
          active: eventTab === 'quotes',
          allowNew: permissionToCreateQuote
        },
        { label: 'Invoices', value: 'invoices', active: eventTab === 'invoices', allowNew: permissionToCreateInvoice },
        crmFlag && !isCrew && { label: 'Tasks', value: 'tasks', active: eventTab === 'tasks' }
      ].filter(Boolean),
    [isMobile, crmFlag, isCrew, objectLabels, eventTab, permissionToCreateQuote, permissionToCreateInvoice]
  )

  const activitiesTabList = useMemo(
    () =>
      [
        { label: 'All Activity', value: 'all', active: activityTab === 'all' },
        { label: 'Chat History', value: 'chat', active: activityTab === 'chat' },
        crmFlag && !isCrew && { label: 'Notes', value: 'notes', active: activityTab === 'notes' },
        crmFlag && !isCrew && { label: 'Files', value: 'files', active: activityTab === 'files' }
      ].filter(Boolean),
    [crmFlag, isCrew, activityTab]
  )

  const quoteListQuery = (type: string) =>
    jsonToLucene({
      filters: [
        {
          type: 'or',
          value: [
            { id: 'contact.id', type: 'equal', value: id },
            { id: 'secondaryContact.id', type: 'equal', value: id }
          ]
        },
        { id: 'is_invoice', type: 'equal', value: type === 'quotes' ? false : true },
        {
          id: 'trashed',
          type: 'equal',
          not: true,
          value: true
        }
      ]
    })

  const dealQuery = jsonToLucene({
    filters: [
      {
        id: 'contact.id',
        type: 'equal',
        value: id
      }
    ]
  })

  const activityQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'in',
        not: true,
        value: ['edited', 'owner-changed', 'message', 'event']
      },
      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      {
        id: 'docIds',
        type: 'in',
        not: false,
        value: [id]
      }
    ]
  })

  const chatQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'equal',
        value: 'message'
      },
      {
        id: 'details.message.deleted',
        type: 'equal',
        not: true,
        value: true
      },
      {
        id: 'details.contact.id',
        type: 'equal' as const,
        value: removeAuth0Prefix(id)
      }
    ]
  })

  const notesQuery = jsonToLucene({
    filters: [
      {
        id: DETAILS_ACTIVITY_TYPE,
        type: 'equal',
        value: 'note'
      },
      {
        id: 'docIds',
        type: 'in',
        not: false,
        value: [contact?._id]
      }
    ]
  })

  async function handleFilesChange(files: QuoteFile[], replaceExisting: boolean = true, fileDoc?: Deal) {
    if (fileDoc) {
      const updatedDeal: Deal = {
        ...fileDoc,
        files: {
          ...(replaceExisting
            ? Object.values(fileDoc?.files ?? {}).reduce((acc, file: QuoteFile) => {
                acc[file.key] = {
                  ...file,
                  deleted: true
                }
                return acc
              }, {})
            : fileDoc?.files),
          ...files.reduce((acc, file) => {
            // if this file key already exists and is delted, use the delted one
            if (fileDoc?.files?.[file.key]?.deleted) {
              acc[file.key] = {
                ...fileDoc?.files[file.key]
              }
            } else {
              acc[file.key] = file
            }

            return acc
          }, {})
        }
      }
      await saveDeal({ deal: updatedDeal, delayedRefetchQueries: ['searchActivities'] })
    } else {
      const updatedContact: Contact = {
        ...contact,
        files: {
          ...(replaceExisting
            ? Object.values(contact?.files ?? {}).reduce((acc, file: QuoteFile) => {
                acc[file.key] = {
                  ...file,
                  deleted: true
                }
                return acc
              }, {})
            : contact?.files),
          ...files.reduce((acc, file) => {
            // if this file key already exists and is delted, use the delted one
            if (contact?.files?.[file.key]?.deleted) {
              acc[file.key] = {
                ...contact?.files[file.key]
              }
            } else {
              acc[file.key] = file
            }

            return acc
          }, {})
        }
      }

      setContact(updatedContact)
      const { contact: savedContact } = await saveContact({
        contact: updatedContact,
        delayedRefetchQueries: ['searchActivities']
      })
      setContact(savedContact)
    }
  }

  async function handleCreateNote(files: QuoteFile[], note: string) {
    // Upload the files to contact, then create the note activity
    const hasNote = striptags(note).trim().length > 0
    const notedFiles = files.map((file) => ({
      ...file,
      onNote: hasNote
    }))
    if (notedFiles.length > 0) {
      await handleFilesChange(notedFiles, false)
    }
    if (hasNote) {
      await createNote({
        docId: contact?._id,
        docType: 'contact',
        note,
        files: files.map((file) => file.key)
      })
    }
  }

  async function handleSaveNote(
    noteDoc: Deal | Contact,
    id: string,
    files: QuoteFile[],
    updatedFiles: QuoteFile[],
    note: string
  ) {
    // Upload the files to contact, then create the note activity
    const notedFiles = [...files, ...updatedFiles].map((file) => ({
      ...file,
      onNote: true
    }))
    await handleFilesChange(notedFiles, false, noteDoc.type === 'contact' ? null : (noteDoc as Deal))
    await updateNote({
      id,
      note,
      files: uniq(files.map((file) => file.key))
    })
    dismissDialog()
  }

  async function handleDeleteNote(note: NoteActivity) {
    const noteDocType = note.details.docType
    openDialog(ConfirmationDialog, {
      title: 'Are you sure?',
      message: `This note ${note?.details?.files?.length > 0 ? 'and its attached files ' : ''}will be permanently deleted from the ${noteDocType} and cannot be undone.`,
      onConfirm: async () => {
        await deleteNote(note._id, noteDocType !== 'contact')
        if (note?.details?.files?.length > 0 && noteDocType === 'contact') {
          const updatedFiles = contactFiles.filter((file) => !note?.details?.files?.includes(file.key))
          handleFilesChange(updatedFiles, true)
        }
        dismissAllDialogs()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditNote(note: NoteActivity) {
    openDialog(NoteDialog, {
      noteId: note._id,
      docId: contact._id,
      onConfirm: async (noteDoc: Contact | Deal, noteText: string, files: QuoteFile[], isDirty?: boolean) => {
        if (isDirty) {
          const removedFiles = note.details.files.filter((fileKey) => !files.some((file) => file.key === fileKey))
          const updatedFiles = getDocFiles(noteDoc).map((file) => {
            if (removedFiles.includes(file.key)) {
              return { ...file, deleted: true }
            }
            return file
          })
          await handleSaveNote(noteDoc, note._id, files, updatedFiles, noteText)
        }
      },
      onCancel: dismissDialog,
      onDelete: async (note: NoteActivity) => {
        await handleDeleteNote(note)
      }
    })
  }

  function handleEditNoteFile(activity: NoteActivity) {
    const noteFiles = activity?.details?.versions[0]?.files.map((file) => {
      return {
        ...file,
        title: file.name,
        src: file.url,
        key: file.id
      }
    }) as QuoteFile[]
    const fileDocId = activity?.details?.docType === 'deal' ? activity?.details?.deal?.id : contact?._id
    openDialog(EditItemFilesDialog, {
      fileDocId: fileDocId,
      loadFiles: true,
      fileDocType: activity?.details?.docType,
      titleOnly: true,
      files: noteFiles as QuoteFile[],
      index: 0,
      onConfirm: async (newFiles, deletedFiles, fileDoc: Contact | Deal) => {
        setActiveDialog(uuid())
        const docFiles = getDocFiles(fileDoc)
        const filesUpdated =
          newFiles.length !== docFiles.length ||
          newFiles.some((file) => {
            const existingFile = docFiles.find((f) => f.key === file.key)
            return existingFile?.title !== file.title || existingFile?.type !== file.type
          })
        if (filesUpdated) {
          const updatedFiles = newFiles.map((file) => {
            // if file is in deletedFiles, mark it as deleted
            const deletedFile = deletedFiles.find((deletedFile) => deletedFile.key === file.key)
            if (deletedFile) {
              return { ...file, deleted: true }
            } else {
              return file
            }
          })
          await handleSaveNote(fileDoc, activity._id, newFiles, updatedFiles, activity.details.note)
        }

        dismissDialog()
      },
      onCancel: dismissDialog,
      onDelete: (_key, _files) => {}
    })
  }

  function handleNoteFileDetails(fileIdString: string): QuoteFile {
    const fileId = fileIdString.split('_')[1]
    const file = contactFiles.find((file) => file.key === fileId)
    return file
  }

  function renderActivityTabContent(activeTab) {
    switch (activeTab.value) {
      case 'all':
        return (
          <NotesDocList
            docId={contact?._id}
            query={activityQuery}
            onSaveNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onEditNoteFiles={handleEditNoteFile}
            onNoteFileDetails={handleNoteFileDetails}
            noResultsMessage="No activity found."
          />
        )
      case 'chat':
        return (
          <DashboardActivity
            query={chatQuery}
            onUpdate={() => {}}
            noResultsMessage="No messages found."
            showContent={true}
            ActivityComponent={ChatActivityComponent}
          />
        )
      case 'notes':
        return (
          <NotesDocList
            docId={contact?._id}
            query={notesQuery}
            onSaveNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onEditNoteFiles={handleEditNoteFile}
            onNoteFileDetails={handleNoteFileDetails}
          />
        )
      case 'files':
        return <FilesDocList docId={contact?._id} files={contactFiles} onUpload={handleFilesChange} />
      default:
        return null
    }
  }

  const activeActivityTab = activitiesTabList.find((tab) => tab.active)

  function handleEventTabClick(tab: string) {
    setUrlQuery({ location, params: { eventTab: tab } })
  }

  function handleActivityTabClick(tab: string) {
    setUrlQuery({ location, params: { activityTab: tab } })
  }

  function handleSetViewTab(tab: 'details' | 'activities') {
    setUrlQuery({
      location,
      params: {
        viewTab: tab
      }
    })
    setViewTab(tab)
  }

  const handleNewItemClick = ({ type }: { type: string }) => {
    dismissDialog()
    switch (type) {
      case 'event':
      case 'events':
        openDialog(EventDialog, {
          isNew: true,
          dealQuery,
          event: {
            id: uuid(),
            contact: {
              id: contact._id,
              firstName: contact.first_name,
              lastName: contact.last_name,
              phoneNumber: contact.phone_number,
              email: contact.email,
              companyId: contact?.companyDocId ?? null
            },
            deal: {
              id: '',
              source: contact?.lead_source ?? null
            }
          },
          onConfirm: async (event: CalendarEvent, eventTemplate: string) => {
            await saveEvent({
              variables: {
                event,
                eventTemplate
              },
              refetchQueries: ['searchActivities']
            })
            setContact((contact) => {
              return {
                ...contact,
                email: event?.contact?.email
              }
            })
            setActiveDialog(uuid())
            handleSetViewTab('details')
            handleEventTabClick('events')
            dismissDialog()
            setTimeout(() => {
              setActivityRefreshToggle((prev) => !prev)
            }, 2000)
          }
        })
        break
      case 'deal':
      case 'deals':
        openDialog(DealDialog, {
          onConfirm: (deal: Deal) => {
            // setActiveDialog(uuid())
            // setTimeout(() => {
            //   setRefreshToggle((prev) => !prev)
            // }, 2000)
            // handleSetViewTab('details')
            // handleEventTabClick('deals')
            goToDeal(deal._id)
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'estimate':
      case 'quotes':
        openDialog(CreateQuoteDialog, {
          invoice: false,
          forContact: contact,
          onConfirm: (quote: QuoteDocument) => {
            history.push(`/quotes/new`, { quote })
            setActiveDialog(uuid())
            setTimeout(() => {
              setRefreshToggle((prev) => !prev)
            }, 500)
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'invoices':
      case 'invoice':
        openDialog(CreateQuoteDialog, {
          invoice: true,
          forContact: contact,
          onConfirm: (quote: QuoteDocument) => {
            history.push(`/invoices/new`, { quote })
            setActiveDialog(uuid())
            setTimeout(() => {
              setRefreshToggle((prev) => !prev)
            }, 500)
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'task':
      case 'tasks':
        openDialog(TaskDialog, {
          dealQuery: dealQuery,
          onConfirm: () => {
            setActiveDialog(uuid())
            handleSetViewTab('details')
            handleEventTabClick('tasks')
            setTimeout(() => {
              setRefreshToggle((prev) => !prev)
            }, 500)
            dismissDialog()
          },
          onCancel: dismissDialog
        })

        break
      case 'note':
      case 'notes':
        openDialog(NoteDialog, {
          docId: contact?._id,
          onConfirm: async (
            _noteDoc: Company | Contact | Deal,
            note: string,
            files: QuoteFile[],
            _isDirty?: boolean
          ) => {
            setActiveDialog(uuid())
            await handleCreateNote(files, note)
            handleSetViewTab('activities')
            handleActivityTabClick('notes')
            dismissDialog()
          },
          onCancel: dismissDialog
        })
        break
      case 'file':
        openDialog(EditItemFilesDialog, {
          fileDocId: contact._id,
          titleOnly: true,
          files: Object.values(contact?.files ?? {}) as QuoteFile[],
          index: Object.values(contact?.files ?? {}).length,
          onConfirm: async (newFiles, _deletedFiles) => {
            setActiveDialog(uuid())
            await handleFilesChange(newFiles)
            handleSetViewTab('activities')
            handleActivityTabClick('files')
            setActivityRefreshToggle((prev) => !prev)
            dismissDialog()
          },
          onCancel: () => {
            dismissDialog()
          }
        })
        break
      case 'file-companycam': {
        const currentFiles = Object.values(contact?.files ?? {}) as QuoteFile[]
        openDialog(EditIntegrationItemFilesDialog, {
          provider: 'companycam',
          maxWidth: 'lg',
          fullWidth: true,
          files: currentFiles,
          onConfirm: (newFiles: QuoteFile[]) => {
            const now = Date.now()
            const existingKeys = new Set(currentFiles.map((f) => f.key))
            const timestampedNewFiles = newFiles
              .filter((f) => !existingKeys.has(f.key))
              .map((f) => ({ ...f, created: f.created ?? now }))
            const mergedFiles = [...currentFiles, ...timestampedNewFiles]
            openDialog(
              EditItemFilesDialog,
              {
                fileDocId: contact._id,
                files: mergedFiles,
                index: currentFiles.length,
                titleOnly: true,
                onConfirm: async (finalFiles, _deletedFiles) => {
                  setActiveDialog(uuid())
                  await handleFilesChange(finalFiles)
                  handleSetViewTab('activities')
                  handleActivityTabClick('files')
                  setActivityRefreshToggle((prev) => !prev)
                  dismissDialog()
                },
                onCancel: dismissDialog
              },
              { replace: true }
            )
          },
          onCancel: dismissDialog
        })
        break
      }
      default:
        break
    }
  }

  let canEdit = false
  if (contact) {
    const isAssigned = isUserAssigned(contact, user.user_id)
    const isOwner = isUserOwner(contact, user.user_id)
    canEdit =
      (isOwner || (isAssigned && permissionToEditAssignedContacts) || permissionToEditAllContacts) && !trialExpired
  }

  useEffect(() => {
    const dataContact = data?.contact ?? null
    if (dataContact) {
      if (!contact || contact._rev !== dataContact._rev) {
        setContact(dataContact)
      }
      setCrmContext({
        subtitle: dataContact?.first_name ? `Contact - ${formatName(dataContact)}` : '',
        contact: { fullContact: dataContact, hide: true },
        deal: { search: { query: dealQuery } }
      })
    }
  }, [data])

  useEffect(() => {
    return () => {
      setCrmContext({})
    }
  }, [])

  const details = useMemo(() => {
    const sourceDetail = getContactSourceDetail({ contact, options, hasSources })

    return [
      contact?.address && {
        title: 'Address',
        value: formatJobAddress({
          jobAddress: {
            address: contact?.address,
            city: contact?.city,
            province: contact?.province,
            postal: contact?.postal
          }
        }),
        canCopy: true,
        link: () => {
          // Handle address click
          const parts = [
            contact?.address ?? '',
            contact?.city ?? '',
            contact?.province ?? '',
            contact?.country ?? '',
            contact?.postal ?? ''
          ].filter((part) => part.trim() !== '') // Remove empty strings
          const formattedString = parts.join(', ')
          openMap(formattedString)
          function openMap(address) {
            const encodedAddress = encodeURIComponent(address)
            if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
              window.open(`maps://?q=${encodedAddress}`, '_blank')
            } else {
              window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank')
            }
          }
        }
      },
      contact?.email && {
        title: 'Email',
        value: contact?.email,
        canCopy: true,
        link: () => window.open(`mailto:${contact?.email}`, '_blank')
      },
      contact?.alternate_email && {
        title: 'Alternate Email',
        value: contact?.alternate_email,
        canCopy: true,
        link: () => window.open(`mailto:${contact?.alternate_email}`, '_blank')
      },
      contact?.phone_number && {
        title: 'Phone',
        value: formatPhoneNumber({
          options,
          phoneNumber: contact?.phone_number,
          extension: contact?.phone_ext
        }),
        canCopy: true,
        link: () => {
          const phoneNumber = formatPhoneNumber({
            options,
            phoneNumber: contact?.phone_number,
            extension: contact?.phone_ext
          })
          const formattedNumber = `tel:${phoneNumber}`
          window.open(formattedNumber, '_blank')
        }
      },
      contact?.alternate_phone && {
        title: 'Alternate Phone',
        value: `${contact?.alternate_phone_ext} ${contact?.alternate_phone}`.trim(),
        canCopy: true,
        link: () => {
          const phoneNumber = `${contact?.alternate_phone_ext} ${contact?.alternate_phone}`.trim().replace(/\D/g, '')
          const formattedNumber = `tel:${phoneNumber}`
          window.open(formattedNumber, '_blank')
        }
      },
      contact?.preferred_contact_method &&
        contact?.preferred_contact_method !== 'any' && {
          title: 'Preferred Contact Method',
          icon: <StarIcon className={classes.preferredStar} />,
          value: (() => {
            switch (contact?.preferred_contact_method) {
              case 'phone':
                return 'Phone'
              case 'email':
                return 'Email'
              case 'sms':
                return 'Text'
              default:
                return ''
            }
          })()
        },
      sourceDetail,
      contact?.companyDocId && {
        title: 'Company',
        value: contact?.company,
        link: () => goToCompany(contact?.companyDocId)
      },
      contact?.internal_note && {
        title: 'Internal Note',
        value: contact?.internal_note
      },
      contact?.created_date && {
        title: 'Date Created',
        value: moment(contact.created_date).format(options.options.dateFormat.momentValue)
      }
    ].filter(Boolean)
  }, [contact, contact?.companyDocId, contact?.company, contact?.email, hasSources, options])

  function renderEventTabContent(activeTab) {
    switch (activeTab.value) {
      case 'details':
        return <DetailDocList details={details} />
      case 'events':
        return (
          <EventDocList
            query={jsonToLucene({
              filters: [
                {
                  id: DETAILS_ACTIVITY_TYPE,
                  type: 'equal',
                  value: 'event'
                },
                { id: 'details.contact.id', type: 'equal', value: id },
                { id: 'trashed', type: 'equal', not: true, value: true }
              ]
            })}
            onEventsChange={() => {
              setTimeout(() => {
                setActivityRefreshToggle((prev) => !prev)
              }, 2000)
            }}
          />
        )
      case 'deals':
        return <DealDocList query={dealQuery} />
      case 'quotes':
        return <QuoteDocList query={quoteListQuery('quotes')} />
      case 'invoices':
        return <QuoteDocList isInvoice query={quoteListQuery('invoices')} />
      case 'tasks':
        return (
          <TaskDocList
            contactId={contact?._id}
            onTasksChange={() =>
              setTimeout(() => {
                setActivityRefreshToggle((prev) => !prev)
              }, 200)
            }
          />
        )
      default:
        return null
    }
  }
  const activeEventTab = eventsTabList.find((tab) => tab.active)

  if (error) {
    Sentry.captureException(error)
    enqueueSnackbar('Unable to load Contact', { variant: 'error' })
    return <Redirect to="/people/contacts" />
  }

  if (contact?.trashed) {
    enqueueSnackbar('This contact has been deleted.', { variant: 'error' })
    return <Redirect to="/people/contacts" />
  }

  function handleDeleteContact() {
    openDialog(ConfirmationDialog, {
      message: (
        <span>
          This will permanently delete{' '}
          {`${contact?.first_name ?? ''}${contact?.last_name ? ' ' + contact.last_name : ''}`}
          {crmFlag ? ` and cancel all their upcoming events.` : '.'}
        </span>
      ),
      onConfirm: async () => {
        deleteContacts([contact._id]).catch((error) => {
          Sentry.captureException(error)
          enqueueSnackbar('Unable to delete Contact', { variant: 'error' })
        })
        setTimeout(() => {
          history.push('/people/contacts', { fullLoad: true })
          dismissDialog()
        }, 1000)
      },
      onCancel: dismissDialog
    })
  }

  function handleEditContact() {
    openDialog(ContactDialog, {
      contactId: id,
      onConfirm: (contact: Contact) => {
        setContact(contact)
        handleSetViewTab('details')

        if (isMobile) {
          handleEventTabClick('details')
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleEditContactTags() {
    openDialog(EditTagsDialog, {
      appliedTags: contact.tags,
      onConfirm: async (tags: string[]) => {
        try {
          const savedContact = await saveContact({
            contact: {
              ...contact,
              tags
            }
          })
          setContact(savedContact.contact)
          setTimeout(() => {
            setActivityRefreshToggle((prev) => !prev)
          }, 2000)
        } catch (error) {
          const message = error?.graphQLErrors?.[0]?.message || 'Unable to save Contact'
          enqueueSnackbar(message, { variant: 'error' })
          Sentry.captureException(error, { tags: { contactId: contact._id } })
        }

        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  function handleDeleteContactTag(tag: QuoteTag) {
    openDialog(ConfirmationDialog, {
      message: <span>This will remove the tag from the contact. You can add it back later if needed.</span>,
      onConfirm: async () => {
        try {
          const { contact: savedContact } = await saveContact({
            contact: {
              ...contact,
              tags: contact.tags.filter((t) => t !== tag.name)
            }
          })
          setContact(savedContact)
        } catch (error) {
          const message = error?.graphQLErrors?.[0]?.message || 'Unable to save Contact'
          enqueueSnackbar(message, { variant: 'error' })
          Sentry.captureException(error, { tags: { contactId: contact?._id } })
        }
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  return (
    <BasePage>
      <DocumentTitle title="Contact" />
      <motion.div key={loading.toString()} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {loading || !contact ? (
          <EditContactShim />
        ) : (
          <>
            <OwnedHeader
              type="contact"
              item={contact}
              roles={['admin', 'sales', 'viewer']}
              canCreateQuotes={permissionToCreateQuote}
              canCreateInvoices={permissionToCreateInvoice}
              handleNewClick={handleNewItemClick}
              onOwnerChange={async (estimatorOption: DashboardEstimator) => {
                if (removeAuth0Prefix(contact.owner?.id) === removeAuth0Prefix(estimatorOption.value)) {
                  return
                } else {
                  const newContact: Contact = {
                    ...contact,
                    owner: {
                      __typename: 'Owner',
                      id: removeAuth0Prefix(estimatorOption.value),
                      firstName: estimatorOption?.label.split(' ')[0],
                      lastName: estimatorOption?.label.split(' ')[1]
                    }
                  }
                  setContact(newContact)
                  await saveContact({ contact: newContact })
                }
              }}
              actions={
                canEdit
                  ? [
                      {
                        key: 'edit',
                        label: 'Edit',
                        value: 'edit'
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        value: 'delete'
                      }
                    ]
                  : []
              }
              handleAction={(action: string) => {
                switch (action) {
                  case 'edit':
                    handleEditContact()
                    break
                  case 'delete':
                    handleDeleteContact()
                    break
                }
              }}
            />
            <div className={classes.body}>
              {/* Large */}
              {isLg && (
                <Grid container spacing={2}>
                  {/* Left */}
                  <Grid item xs={6} className={classes.gridItem} style={{ padding: 0, paddingRight: '8px' }}>
                    <DetailsContainer
                      classes={{
                        paper: 'contact-page-details-card'
                      }}
                      details={details}
                      handleEdit={canEdit ? handleEditContact : null}
                    />
                    {hasTags && (
                      <TagContainer
                        tags={contactTags}
                        handleEdit={handleEditContactTags}
                        handleDelete={handleDeleteContactTag}
                      />
                    )}
                    <DocsContainer
                      handleTabClick={handleEventTabClick}
                      handleNewClick={handleNewItemClick}
                      tabs={eventsTabList}
                    >
                      <React.Fragment key={`${activeEventTab?.value}-${refreshToggle}-${activeDialog}`}>
                        {renderEventTabContent(activeEventTab)}
                      </React.Fragment>
                    </DocsContainer>
                  </Grid>
                  {/* Right */}
                  <Grid item xs={6} className={classes.gridItem} style={{ padding: 0, paddingLeft: '8px' }}>
                    <MapContainer item={contact} geocode={contact.geocode} />
                    <DocsContainer
                      classes={{ contactDocsContainer: 'activity-container' }}
                      handleTabClick={handleActivityTabClick}
                      tabs={activitiesTabList}
                    >
                      <React.Fragment key={`${activeActivityTab?.value}-${activityRefreshToggle}`}>
                        {renderActivityTabContent(activeActivityTab)}
                      </React.Fragment>
                    </DocsContainer>
                  </Grid>
                </Grid>
              )}
              {/* Small */}
              {!isLg && (
                <Grid item xs={12} className={classes.gridItem}>
                  <div className={classes.tabs}>
                    <Tabs
                      value={viewTab}
                      onChange={(ev, newTab) => {
                        handleSetViewTab(newTab)
                      }}
                    >
                      <Tab value="details" label="Overview" />
                      <Tab disabled label="|" />
                      <Tab value="activities" label="Timeline" />
                    </Tabs>
                  </div>
                  {viewTab === 'details' ? (
                    <>
                      <DocsContainer
                        handleTabClick={handleEventTabClick}
                        handleNewClick={handleNewItemClick}
                        tabs={eventsTabList}
                      >
                        <React.Fragment key={`${activeEventTab?.value}-${refreshToggle}-${activeDialog}`}>
                          {renderEventTabContent(activeEventTab)}
                        </React.Fragment>
                      </DocsContainer>
                      {hasTags && (
                        <TagContainer
                          tags={contactTags}
                          handleEdit={handleEditContactTags}
                          handleDelete={handleDeleteContactTag}
                        />
                      )}
                    </>
                  ) : (
                    <DocsContainer handleTabClick={handleActivityTabClick} tabs={activitiesTabList}>
                      <React.Fragment key={`${activeActivityTab?.value}-${activityRefreshToggle}`}>
                        {renderActivityTabContent(activeActivityTab)}
                      </React.Fragment>
                    </DocsContainer>
                  )}

                  {crmFlag && (
                    <Hidden smDown>
                      <MapContainer item={contact} geocode={contact.geocode} />
                    </Hidden>
                  )}
                </Grid>
              )}
            </div>
          </>
        )}
      </motion.div>
    </BasePage>
  )
}

export default EditContact
